import { createSelector } from '@reduxjs/toolkit';

import { selectConversationsQuota } from '../conversationsQuota';
import {
  oneChatSettingsModel,
  selectOneChatSettings,
} from '../oneChatSettings';
import { selectFlags } from '../flags';
import { CurrentStatus } from '../oneChatSettings/model';
import { selectMessages } from '../messages';
import { selectFaqQuestions } from '../faqQuestions';

const { Appearance } = oneChatSettingsModel;

export const canShowWidgetByAppearance = createSelector(
  [
    selectOneChatSettings.appearance,
    (_: unknown, isMobile: boolean) => isMobile,
  ],
  (appearance, isMobile) => {
    switch (appearance) {
      case Appearance.DESKTOP:
        return !isMobile;
      case Appearance.MOBILE:
        return isMobile;
      case Appearance.DESKTOP_AND_MOBILE:
        return true;
      default:
        return false;
    }
  },
);

export const maybeWidgetOnline = createSelector(
  selectOneChatSettings.currentStatus,
  selectConversationsQuota.isExceeding,
  (status, isExceeding) => {
    if (!status || isExceeding === undefined) {
      return;
    }

    switch (status) {
      case CurrentStatus.OFFLINE:
        return false;
      case CurrentStatus.MANUAL_ONLINE:
        return true;
      case CurrentStatus.AI_ASSISTANT_ONLINE:
        return !isExceeding;
    }
  },
);

export const shouldShowWidgetByAvailability = createSelector(
  selectOneChatSettings.offlineBehavior,
  maybeWidgetOnline,
  (behavior, isOnline) => {
    return (
      isOnline || (behavior !== undefined && behavior.hideWidget === false)
    );
  },
);

export const shouldShowWidget = createSelector(
  [
    shouldShowWidgetByAvailability,
    selectOneChatSettings.appearance,
    (_: unknown, isMobile: boolean) => isMobile,
  ],
  (showByAvailability, appearance, isMobile) => {
    const showByAppearance =
      appearance === Appearance.DESKTOP_AND_MOBILE ||
      (appearance === Appearance.DESKTOP && !isMobile) ||
      (appearance === Appearance.MOBILE && isMobile);

    return showByAvailability && showByAppearance;
  },
);

const contactOrHasForm = createSelector(
  selectFlags.isContact,
  selectMessages.hasContactForm,
  (isContact, hasContactForm) => isContact || hasContactForm,
);

export const manualShouldShowContactForm = createSelector(
  selectOneChatSettings.isManual,
  selectOneChatSettings.manualContactFormBefore,
  contactOrHasForm,
  (isManual, behaviorRequiresForm, noNeedContactForm) =>
    !noNeedContactForm && isManual && behaviorRequiresForm,
);

export const aiShouldShowContactForm = createSelector(
  selectOneChatSettings.isAi,
  selectOneChatSettings.aiContactFormBefore,
  contactOrHasForm,
  (isAi, behaviorRequiresForm, noNeedContactForm) =>
    !noNeedContactForm && isAi && behaviorRequiresForm,
);

export const offlineShouldShowContactForm = createSelector(
  maybeWidgetOnline,
  shouldShowWidgetByAvailability,
  selectOneChatSettings.offlineBehavior,
  contactOrHasForm,
  (online, show, behavior, noNeedContactForm) =>
    !online && show && !noNeedContactForm && Boolean(behavior?.showContactForm),
);

export const shouldShowContactFormInIntroSequence = createSelector(
  manualShouldShowContactForm,
  aiShouldShowContactForm,
  offlineShouldShowContactForm,
  (manual, ai, offline) => manual || ai || offline,
);

export const shouldSendContactFormAfterFirstMessage = createSelector(
  selectOneChatSettings.isManual,
  selectFlags.isContact,
  selectOneChatSettings.manualContactFormAfterMessage,
  selectMessages.noContactFormOnLastPage,
  (
    isManual,
    isContact,
    manualContactFormAfterMessage,
    noContactFormOnLastPage,
  ) =>
    !isContact &&
    isManual &&
    manualContactFormAfterMessage &&
    noContactFormOnLastPage,
);

export const offlineDisabledMessaging = createSelector(
  maybeWidgetOnline,
  selectOneChatSettings.offlineBehavior,
  (online, behavior) =>
    !online && behavior?.hideWidget === false && behavior?.allowChat === false,
);

export const shouldDisableInputWhenOffline = createSelector(
  maybeWidgetOnline,
  shouldShowWidgetByAvailability,
  selectOneChatSettings.offlineBehavior,
  (online, show, behavior) => !online && show && !behavior?.allowChat,
);

export const shouldDisableInputWhenManual = createSelector(
  selectOneChatSettings.isManual,
  selectOneChatSettings.manualAllowChat,
  selectFlags.isContact,
  selectOneChatSettings.manualContactFormBefore,
  (isManual, allowChat, isContact, manualContactFormBefore) =>
    isManual && manualContactFormBefore && !allowChat && !isContact,
);

export const shouldDisableInput = createSelector(
  shouldDisableInputWhenOffline,
  shouldDisableInputWhenManual,
  (offlineDisabled, manualDisabled) => offlineDisabled || manualDisabled,
);

export const aiIntroMessage = createSelector(
  selectOneChatSettings.aiIntroMessageSettings,
  selectOneChatSettings.currentStatus,
  maybeWidgetOnline,
  (introMessageSettings, status, online) =>
    status === CurrentStatus.AI_ASSISTANT_ONLINE &&
    online &&
    introMessageSettings?.enabled &&
    introMessageSettings?.text,
);

export const aiLegalMessage = createSelector(
  selectOneChatSettings.aiLegalMessageSettings,
  selectOneChatSettings.currentStatus,
  maybeWidgetOnline,
  (legalMessageSettings, status, online) =>
    status === CurrentStatus.AI_ASSISTANT_ONLINE &&
    online &&
    legalMessageSettings?.enabled &&
    legalMessageSettings?.text,
);

export const engageMessage = createSelector(
  selectOneChatSettings.engageMessageSettings,
  maybeWidgetOnline,
  (engageMessageSettings, online) =>
    online && engageMessageSettings?.enabled && engageMessageSettings?.text,
);

export const offlineIntroMessage = createSelector(
  selectOneChatSettings.offlineIntroMessageSettings,
  maybeWidgetOnline,
  (offlineMessageSettings, online) =>
    !online && offlineMessageSettings?.enabled && offlineMessageSettings?.text,
);

export const manualIntroMessage = createSelector(
  selectOneChatSettings.manualIntroMessageSettings,
  selectOneChatSettings.currentStatus,
  (introMessageSettings, status) =>
    status === CurrentStatus.MANUAL_ONLINE &&
    introMessageSettings?.enabled &&
    introMessageSettings?.text,
);

export const shouldAutoOpenWidget = createSelector(
  selectOneChatSettings.autoOpen,
  maybeWidgetOnline,
  (autoOpen, isOnline) => Boolean(autoOpen && isOnline),
);

export const aiSuggestions = createSelector(
  selectOneChatSettings.suggestionsSettings,
  selectOneChatSettings.currentStatus,
  maybeWidgetOnline,
  (suggestionsSettings, status, online) =>
    status === CurrentStatus.AI_ASSISTANT_ONLINE &&
    online &&
    suggestionsSettings?.enabled &&
    suggestionsSettings?.questions &&
    suggestionsSettings?.questions.length > 0
      ? suggestionsSettings.questions
      : undefined,
);

export const aiSuggestionsIntroMessage = createSelector(
  selectOneChatSettings.suggestionsSettings,
  selectOneChatSettings.currentStatus,
  maybeWidgetOnline,
  (suggestionsSettings, status, online) =>
    status === CurrentStatus.AI_ASSISTANT_ONLINE &&
    online &&
    suggestionsSettings?.enabled &&
    suggestionsSettings?.introduction?.text
      ? suggestionsSettings.introduction.text
      : undefined,
);

export const shouldSendSuggestions = createSelector(
  selectMessages.shouldSendSuggestions,
  aiSuggestions,
  (shouldSend, suggestions) => shouldSend && Boolean(suggestions),
);

export const faqQuestionsData = createSelector(
  selectOneChatSettings.faqQuestionsEnabled,
  selectOneChatSettings.isManual,
  selectFaqQuestions.questions,
  (enabled, isManual, questions) =>
    isManual && enabled && questions.length > 0 ? questions : undefined,
);

export const faqQuestionsIntroMessage = createSelector(
  selectOneChatSettings.faqQuestionsIntroText,
  selectOneChatSettings.isManual,
  selectOneChatSettings.faqQuestionsEnabled,
  (text, isManual, enabled) => (isManual && enabled ? text : undefined),
);

export const shouldSendFaqButtons = createSelector(
  selectMessages.shouldSendFaqButtons,
  faqQuestionsData,
  (shouldSend, questions) => shouldSend && !!questions?.length,
);
